import React, { useState } from "react";
import { Download, Share, Plus, X, Smartphone, Monitor } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { toast } from "sonner";

/**
 * Always-visible "Install App" button. Adapts to device:
 *   - Android/Chrome/Edge with fired `beforeinstallprompt` → triggers native prompt
 *   - iOS Safari → opens a modal with "Share → Add to Home Screen" instructions
 *   - Chrome without deferred prompt yet → opens a modal with browser-specific hint
 *   - Already installed → hidden
 */
export default function InstallButton({ compact = false }) {
  const { installable, installed, ios, install } = useInstallPrompt();
  const [helpOpen, setHelpOpen] = useState(false);

  if (installed) return null; // hide when running as installed PWA

  const onClick = async () => {
    if (installable) {
      const res = await install();
      if (res.outcome === "accepted") toast.success("Installing HHL Estates…");
      else if (res.outcome === "dismissed") toast("Install cancelled");
      return;
    }
    // No native prompt available (iOS Safari, or Chrome hasn't fired yet)
    setHelpOpen(true);
  };

  const label = compact ? "Install" : "Install App";

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        data-testid="install-app-btn"
        aria-label="Install HHL Estates as an app"
        className={
          compact
            ? "inline-flex items-center justify-center border border-zinc-300 h-9 w-9 sm:h-10 sm:w-10 hover:bg-black hover:text-white hover:border-black transition-colors"
            : "w-full inline-flex items-center justify-center gap-2 border border-zinc-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-black hover:text-white hover:border-black transition-colors"
        }
        title="Install app on your device"
      >
        <Download className={compact ? "h-4 w-4" : "h-3.5 w-3.5"} />
        {!compact && <span>{label}</span>}
      </button>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="rounded-none border-zinc-300 max-w-md" data-testid="install-help-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tight">
              Install HHL Estates
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-600">
              Add the app to your home screen for full-screen, app-like access.
            </DialogDescription>
          </DialogHeader>

          {ios ? (
            <div className="space-y-4" data-testid="install-help-ios">
              <div className="flex items-center gap-3 p-3 border border-zinc-200 bg-zinc-50">
                <Smartphone className="h-5 w-5 shrink-0" />
                <div className="text-xs text-zinc-700">iPhone / iPad · Safari</div>
              </div>
              <ol className="space-y-3 text-sm text-zinc-800">
                <li className="flex items-start gap-3">
                  <span className="shrink-0 h-6 w-6 bg-black text-white text-xs font-bold flex items-center justify-center">1</span>
                  <span>Tap the <span className="inline-flex items-center gap-1 bg-zinc-100 border border-zinc-200 px-1.5 py-0.5 text-xs"><Share className="h-3 w-3" /> Share</span> button in Safari's toolbar.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="shrink-0 h-6 w-6 bg-black text-white text-xs font-bold flex items-center justify-center">2</span>
                  <span>Scroll down and tap <span className="inline-flex items-center gap-1 bg-zinc-100 border border-zinc-200 px-1.5 py-0.5 text-xs"><Plus className="h-3 w-3" /> Add to Home Screen</span>.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="shrink-0 h-6 w-6 bg-black text-white text-xs font-bold flex items-center justify-center">3</span>
                  <span>Tap <b>Add</b> — the HHL app icon will appear on your home screen.</span>
                </li>
              </ol>
              <div className="text-[11px] text-zinc-500 border-t border-zinc-200 pt-3">
                Note: Only works in <b>Safari</b> on iOS — Chrome/Firefox on iOS don't support this yet.
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="install-help-android">
              <div className="flex items-center gap-3 p-3 border border-zinc-200 bg-zinc-50">
                <Monitor className="h-5 w-5 shrink-0" />
                <div className="text-xs text-zinc-700">Android · Chrome / Edge (or Desktop Chrome / Edge)</div>
              </div>
              <ol className="space-y-3 text-sm text-zinc-800">
                <li className="flex items-start gap-3">
                  <span className="shrink-0 h-6 w-6 bg-black text-white text-xs font-bold flex items-center justify-center">1</span>
                  <span>Tap the <b>menu</b> icon (⋮) in your browser's top-right corner.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="shrink-0 h-6 w-6 bg-black text-white text-xs font-bold flex items-center justify-center">2</span>
                  <span>Choose <b>Install app</b>, <b>Add to Home screen</b>, or <b>Apps → Install this site</b>.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="shrink-0 h-6 w-6 bg-black text-white text-xs font-bold flex items-center justify-center">3</span>
                  <span>Confirm <b>Install</b> — the app opens full-screen with its own home-screen icon.</span>
                </li>
              </ol>
              <div className="text-[11px] text-zinc-500 border-t border-zinc-200 pt-3">
                Tip: If you don't see the option, refresh the page once — the browser needs to detect the app manifest first.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
